import { useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import { useGetRecentMeshRunsQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

export const useAllRecentMeshRuns = ({
  organizationId,
  skip,
}: {
  organizationId: string;
  // true when the caller has no use for the runs
  skip: boolean;
}) => {
  const today = new Date();
  const flags = useFeatureFlags();

  // we want somewhat stable value for current time.
  // otherwise we end up in circular refetching.
  // so instead of using current time, we round to next minute.
  today.setMinutes(today.getMinutes() + 1);
  today.setSeconds(0);
  today.setMilliseconds(0);

  const threeDaysAgo = new Date(today);
  threeDaysAgo.setDate(today.getDate() - 3);
  const startDate = commonDate.dateToEpoch(threeDaysAgo);
  const endDate = commonDate.dateToEpoch(today);

  const { data, loading, error, refetch } = useGetRecentMeshRunsQuery({
    skip,
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
      startDate,
      endDate,
    },
  });

  const recentMeshRuns = useMemo(() => {
    const meshRuns = data?.meshRuns || [];
    const meshRecords: DataPipeline.MeshRunRecord[] = meshRuns.map(
      DbGraphUtils.deserializeMeshRunRecord,
    );
    return meshRecords;
  }, [data?.meshRuns]);

  return {
    recentMeshRuns,
    loading,
    error,
    refetch,
  };
};
